/** @module The frame of sign-in and registration: brand, heading, the form, a note below. */
import { Eye, EyeOff } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { BrandMark } from "@/components/brand";

export function AuthLayout({
  title,
  lead,
  badge,
  footer,
  children,
}: {
  title: string;
  lead: string;
  /** E.g. "Einladung von Tom" above the heading. */
  badge?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-sm flex-col px-6 pt-10 pb-6">
      <div className="flex flex-col gap-[6px] pt-6 pb-6">
        <BrandMark size={44} className="mb-3" />
        {badge}
        <h1 className="t-h1">{title}</h1>
        <p className="t-muted">{lead}</p>
      </div>
      {children}
      <span className="grow" />
      {footer ? <div className="t-sm t-muted pt-8 text-center">{footer}</div> : null}
    </div>
  );
}

/** A password field with the design's eye: show what was typed, on request. */
export function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  describedBy,
}: {
  /** The label points here; it must not wrap the field, or the eye joins the name. */
  id: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
  describedBy?: string;
}) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative block">
      <input
        id={id}
        type={visible ? "text" : "password"}
        required
        autoComplete={autoComplete}
        aria-describedby={describedBy}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="input w-full pr-12"
      />
      <button
        type="button"
        className="absolute top-1/2 right-1 grid size-10 -translate-y-1/2 place-items-center text-(--text-muted)"
        aria-label={visible ? t("auth.hidePassword") : t("auth.showPassword")}
        aria-pressed={visible}
        onClick={() => {
          setVisible((current) => !current);
        }}
      >
        {visible ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
      </button>
    </span>
  );
}
